import { useGraphQLQuery } from '@betterment/graphql';
import { useSessionStorage } from '@betterment/js-runtime';
import { Button, H1, Icon, IconName, ListRow, Text, VStack } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import Footer from '../../../../Shared/components-v2/footer/Footer';
import { ProductCategory } from '../../../data';
import { useCompleteDraftPurposeFlow } from '../../hooks/useCompleteDraftPurposeFlow';
import { retirementQuestionWithOptions, useRetirementStep } from '../FlowConfig';
import { RetirementPurposeCreationOptionDocument } from '../graphql/query.retirementPurposeCreationOption.generated';

type FormValues = Yup.InferType<ReturnType<typeof retirementQuestionWithOptions>>;

export const RetirementSelectionStep: React.FC = () => {
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId: string }>();
  const { commit, dependencies } = useRetirementStep('retirementSelection');

  const { data } = useGraphQLQuery(RetirementPurposeCreationOptionDocument, {
    throwOnError: true,
  });

  // This is a temporary solution until we have a more permanent way to persist the product category
  // through different flows. We want to persist the product category so we can pass it to the AddGoalFlow.
  const { values: productCategory } = useSessionStorage<ProductCategory>('purpose-flow-product-category');

  const { completeFlow } = useCompleteDraftPurposeFlow({
    draftEnvelopeId: flowId,
    name: dependencies.nameAndImage.name,
    imageType: dependencies.nameAndImage.imageType,
    productCategory,
  });

  if (!data) return null;

  const retirementOptions = data.purposeCreationOptions
    .filter(
      option =>
        option.__typename === 'RetirementGrowthPurposeCreationOption' ||
        option.__typename === 'RetirementIncomePurposeCreationOption'
    )
    .map(option => ({
      id: option.id,
      type: option.legacyAdviceTypeId,
      title: option.name,
      description: option.description,
      icon: option.__typename === 'RetirementGrowthPurposeCreationOption' ? 'Briefcase' : 'BeachUmbrella',
    }));

  const handleSelect = async (selectedRetirement: FormValues['selectedRetirement']) => {
    commit({ selectedRetirement });

    if (selectedRetirement === 'retirement_income') {
      await completeFlow({
        purposeSubType: selectedRetirement,
        purposeInput: {
          retirementIncome: {},
        },
      });

      return;
    }

    void navigate(`/add_purpose/retirement/${flowId}/retirement_details`);
  };

  return (
    <WorkflowLayout pageTitle="Retirement">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate(-1)} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <H1>Where are you at with retirement?</H1>
          <VStack>
            {retirementOptions.map(({ id, type, title, description, icon }) => (
              <Button
                key={id}
                onClick={() => handleSelect(type as FormValues['selectedRetirement'])}
                variant="textSecondary"
                css={{
                  width: '100%',
                  textDecoration: 'none',
                  pt: 'space.300',
                  pb: 'space.300',
                  _first: {
                    borderBottom: '1px solid',
                    borderColor: 'border.weak',
                  },
                }}
              >
                <ListRow css={{ p: '0' }}>
                  <ListRow.LeadingAsset>
                    <Icon name={icon as IconName} size="small" />
                  </ListRow.LeadingAsset>
                  <ListRow.LeadingContent>
                    <VStack css={{ display: 'grid' }}>
                      <Text>{title}</Text>
                      <Text variant="text50" color="text.weak">
                        {description}
                      </Text>
                    </VStack>
                  </ListRow.LeadingContent>
                  <ListRow.TrailingAsset>
                    <Icon name="CaretRight" size="small" />
                  </ListRow.TrailingAsset>
                </ListRow>
              </Button>
            ))}
          </VStack>
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
